import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Calendar, Star, Heart } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from './GlassCard';

export default function Earnings() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, tipsRes] = await Promise.all([
          fetch(`${API}/stats/vendor`, { headers: getAuthHeaders() }),
          fetch(`${API}/tips/me/received`, { headers: getAuthHeaders() }),
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (tipsRes.ok) setTips(await tipsRes.json());
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="earnings-title">Earnings</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Track your revenue, tips, and performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        {[
          { label: 'Total Earnings', value: `$${stats?.total_earnings || 0}`, icon: DollarSign, gradient: 'from-[#8B5CF6] to-[#6D28D9]' },
          { label: 'Jobs Completed', value: stats?.completed_bookings || 0, icon: Calendar, gradient: 'from-[#3B82F6] to-[#1D4ED8]' },
          { label: 'Average Rating', value: stats?.rating?.toFixed(1) || 'N/A', icon: Star, gradient: 'from-[#8B5CF6] to-[#EC4899]' },
          { label: 'Tips Received', value: `$${(tips?.total_earned || 0).toFixed(2)}`, sub: `${tips?.count || 0} tip${tips?.count === 1 ? '' : 's'} · 100% yours`, icon: Heart, gradient: 'from-[#F472B6] to-[#EC4899]' },
        ].map((stat, i) => (
          <motion.div key={i} className={`relative bg-gradient-to-br ${stat.gradient} rounded-2xl p-6 overflow-hidden hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all duration-300`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-6 translate-x-6" />
            <div className="relative z-10">
              <stat.icon size={24} className="text-white/80 mb-3" />
              <span className="block text-2xl font-bold text-white mb-1">{stat.value}</span>
              <span className="text-white/70 text-xs font-medium block">{stat.label}</span>
              {stat.sub && <span className="text-white/60 text-[10px] mt-1 block">{stat.sub}</span>}
            </div>
          </motion.div>
        ))}
      </div>

      {tips?.tips?.length > 0 && (
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 overflow-hidden" data-testid="tips-list">
          <div className="p-4 border-b border-white/10 flex items-center gap-2">
            <Heart size={14} className="text-[#F472B6]" fill="currentColor" />
            <h3 className="font-syne font-semibold text-white">Recent Tips</h3>
          </div>
          <div className="divide-y divide-white/5">
            {tips.tips.slice(0, 10).map((t, i) => (
              <div key={i} className="p-3 flex items-center justify-between text-sm" data-testid={`tip-row-${i}`}>
                <div>
                  <p className="text-white text-xs">Booking {t.booking_id?.slice(0, 10) || ''}</p>
                  <p className="text-[#64748B] text-[10px]">{t.confirmed_at ? new Date(t.confirmed_at).toLocaleDateString() : ''}</p>
                </div>
                <p className="font-mono font-bold text-[#F472B6]">+${Number(t.amount || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
